import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { getApiToken } from './authUtils';
import UserSelect from './helpers/UserSelect'; // Import the UserSelect component
import NameNormalization from './components/NameNormalization'; // Import the NameNormalization component
import CatalogManager from './components/CatalogManager'; // Import the new component
import MissingCatalogDashboard from './components/MissingCatalogDashboard';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  Tabs, 
  Tab, 
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { usePermissions } from './rbac/PermissionsProvider';
import { PERMISSIONS } from './rbac/permissions';
import AccessControl from './rbac/AccessControl';
import urlconfig from './config/urlconfig';
import UsersManagement from './UsersManagement';
import ClientRegistration from './components/ClientRegistration';
import UniqueClients from './components/UniqueClients';
import UniqueVendors from './components/UniqueVendors';
import AlertsManager from './components/AlertsManager';

const AdminTools = () => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  const [mismatchedNames, setMismatchedNames] = useState([]);
  const [corrections, setCorrections] = useState({});
  const { can } = usePermissions();
  // The route is already restricted to ADMIN_TOOLS; this keeps the page safe if it is rendered elsewhere.
  const isAdmin = can(PERMISSIONS.ADMIN_TOOLS);

  const dataTabs = [
    { label: '🏢 Unique Clients', content: <UniqueClients /> },
    { label: '🚚 Unique Vendors', content: <UniqueVendors /> },
    { label: '🔔 Alerts', content: <AlertsManager /> },
    { label: '🔐 Access Control', content: <AccessControl /> },
  ];
  const [tabValue, setTabValue] = useState(0);

  const handleScanNames = async () => {
    setLoading(true);
    setError(null);
    setSummary(null);
    setMismatchedNames([]);
    setCorrections({});

    try {
      const account = accounts[0];
      if (!account) throw new Error('No active account. Please log in.');
      const accessToken = await getApiToken(instance, account);

      const response = await fetch('/api/v2/admin/mismatched-names', {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch mismatched names.');
      }

      const result = await response.json();
      setMismatchedNames(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCorrectionChange = (oldName, newName) => {
    setCorrections(prev => ({
      ...prev,
      [oldName]: newName,
    }));
  };

  const handleSubmitCorrections = async () => {
    setLoading(true);
    setError(null);
    setSummary(null);

    const correctionPayload = Object.entries(corrections)
      .filter(([, newName]) => newName) // Only include entries where a new name is selected
      .map(([oldName, newName]) => ({ oldName, newName }));

    if (correctionPayload.length === 0) {
      setError("No corrections have been selected.");
      setLoading(false);
      return;
    }

    try {
      const account = accounts[0];
      if (!account) throw new Error('No active account. Please log in.');
      const accessToken = await getApiToken(instance, account);

      const response = await fetch(`${urlconfig.backend_api_base_url}/api/v2/admin/update-names`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(correctionPayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update names.');
      }

      const result = await response.json();
      setSummary(result);
      setMismatchedNames([]); // Clear the list after successful submission
      setCorrections({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: 'auto' }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'center' }}>
        Admin Tools
      </Typography>

      {isAdmin && (
        <Box>
        <Tabs
            value={Math.min(tabValue, dataTabs.length - 1)}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="fullWidth"
            sx={{
              mb: 3,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 1,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
              },
            }}
          >
            {dataTabs.map(t => <Tab key={t.label} label={t.label} />)}
          </Tabs>
           {dataTabs[Math.min(tabValue, dataTabs.length - 1)].content}
           </Box>
      )}
     

      {isAdmin && (
      <UsersManagement/>
      )}

      {/* {isAdmin && (
        <ClientRegistration/>        
      )} */}

      {/* --- POC Correction Tool (Collapsible) --- */}
      {isAdmin && (
          <Accordion defaultExpanded sx={{ boxShadow: 3, borderRadius: 2, overflow: 'hidden', mb: 4 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="poc-tool-content"
              id="poc-tool-header"
              sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}
            >
              <Typography variant="h6" component="h2">
                POC Name Correction Tool
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Scan the database for Sales POC and SCM POC names that do not match any user in Azure AD. You can then manually select the correct user for each mismatched name and update all relevant records in bulk.
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleScanNames}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {loading ? 'Scanning...' : 'Scan for Mismatched Names'}
                </Button>
              </Box>

              {error && (
                <Alert severity="error" sx={{ my: 2 }}>
                  {error}
                </Alert>
              )}

              {mismatchedNames.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Mismatched Names Found</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Name in Database</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '50%' }}>Correct Name from Azure AD</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mismatchedNames.map(name => (
                          <TableRow key={name}>
                            <TableCell>{name}</TableCell>
                            <TableCell>
                              <UserSelect
                                label="Select Correct User"
                                value={corrections[name] || ''}
                                onChange={(newValue) => handleCorrectionChange(name, newValue)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleSubmitCorrections}
                      disabled={loading || Object.keys(corrections).length === 0}
                    >
                      Submit Corrections
                    </Button>
                  </Box>
                </>
              )}

              {summary && (
                <Box sx={{ mt: 4 }}>
                  <Alert severity="success">
                    <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                      Correction Complete
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText primary="Correction Pairs Applied" secondary={summary.correctionPairsApplied} />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText primary="Total Records Updated in Database" secondary={summary.totalRecordsUpdated} />
                      </ListItem>
                    </List>
                  </Alert>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
      )}

      {/* --- Name Normalization Tool (Collapsible) --- */}
      {isAdmin && (
          <Accordion sx={{ boxShadow: 3, borderRadius: 2, overflow: 'hidden', mb: 4 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="name-normalization-content"
              id="name-normalization-header"
              sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}
            >
              <Typography variant="h6" component="h2">
                Name Normalization Tool
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
                 {/* NameNormalization has its own internal padding, so we set p:0 here */}
                <NameNormalization />
            </AccordionDetails>
          </Accordion>
      )}

      {/* --- NEW: Missing Catalog Info Tool (Collapsible) --- */}
      
            <Accordion sx={{ boxShadow: 3, borderRadius: 2, overflow: 'hidden', mb: 4 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="missing-catalog-info-content"
                id="missing-catalog-info-header"
                sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}
              >
                <Typography variant="h6" component="h2">
                  Missing Catalog Information (ESIN & Ecom Name)
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                  <MissingCatalogDashboard />
              </AccordionDetails>
            </Accordion>
      

      {/* --- Cost Sheet Catalog Manager --- */}
      <Paper sx={{ boxShadow: 3, borderRadius: 2 }}>
        <CatalogManager />
      </Paper>
      {!isAdmin && (
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
                Some advanced administrative utilities are restricted to system administrators.
              </Typography>
      )}
    </Box>
  );
};

export default AdminTools;